import * as React from 'react'

import { useDomainsList } from './hooks'
import { CButton, CCardBody } from '@coreui/react-pro'
import { path } from '../../../libs'
import { toPath } from '../../../features/router'
import { Button, Input, InputRef, Space, Table, Tag } from 'antd'
import { deleteDomainEvent, syncDomainsList } from './model'
import { ColumnsType } from 'antd/lib/table'
import CIcon from '@coreui/icons-react'
import { cilDelete } from '@coreui/icons'
import { Domain } from './types'
import { SearchOutlined } from '@ant-design/icons'
import { useRef, useState } from 'react'
import { FilterConfirmProps, FilterValue } from 'antd/es/table/interface'
import Highlighter from 'react-highlight-words'

type DataIndex = keyof Domain

export const DomainList = () => {
  const { domains, loading, onChangeDomainsList } = useDomainsList()

  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')
  const [orderBy, setOrderBy] = useState('')
  const [orderDirection, setOrderDirection] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    currentSearchText,
    currentSearchedColumn,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    currentSearchText?: string
    currentSearchedColumn?: string
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentSearchText) {
      setSearchText(currentSearchText)
    }
    if (currentSearchedColumn) {
      setSearchedColumn(currentSearchedColumn)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    onChangeDomainsList({
      limit: currentPageSize || pageSize,
      page: currentPage || page,
      orderBy: currentOrderBy || orderBy,
      orderDirection: currentOrderDirection || orderDirection,
      searchText: currentSearchText || searchText,
      searchedColumn: currentSearchedColumn || searchedColumn,
    })
  }
  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex,
  ) => {
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
    handleTableChange({
      currentSearchText: selectedKeys[0],
      currentSearchedColumn: dataIndex,
    })
  }

  const handleReset = (clearFilters: () => void) => {
    clearFilters()
    setSearchText('')
  }

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const columns: ColumnsType<Domain> = [
    {
      title: 'Domain',
      dataIndex: 'domain',
      key: 'domain',
      sorter: true,
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 100,
      render: (item) => {
        return (
          <>
            <CButton
              color="danger"
              className="me-2"
              title={'Delete'}
              onClick={() => {
                if (
                  window.confirm('Are you sure you wish to delete this item?')
                )
                  deleteDomainEvent(item.domain)
              }}
            >
              <CIcon icon={cilDelete} />
            </CButton>
          </>
        )
      },
    },
  ]

  return (
    <CCardBody>
      <CButton
        color="secondary"
        className="mb-2 ms-3"
        onClick={() => {
          syncDomainsList()
        }}
        download="coreui-table-data.csv"
        target="_blank"
      >
        Sync forbidden domains list
      </CButton>
      <CButton
        color="success"
        className="mb-2 ms-3"
        onClick={() => {
          toPath(path.forbiddenDomain.create())
        }}
        download="coreui-table-data.csv"
        target="_blank"
      >
        Create new
      </CButton>
      <Table
        dataSource={domains}
        columns={columns}
        bordered={true}
        scroll={{ x: true }}
        rowKey="domainId"
        loading={loading}
      ></Table>
    </CCardBody>
  )
}
